import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnChanges,
  OnInit,
  output,
  viewChild,
  DestroyRef
} from '@angular/core';

import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Attachment } from '@ui/shared/model';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';

import {
  ImmomioDropdown,
  ImmomioDropdownTrigger,
  ImmomioDropdownMenu,
  ImmomioDropdownGroup,
  ImmomioDropdownItem
} from '@immomio/ui/dropdown';
import { ImmomioIconButtonComponent } from '@immomio/ui/button';
import { mixinAttachmentHelper } from '../attachment-component-helper.mixin';
import { FileSizePipe } from '../../../../pipes/file-size.pipe';
import { SimpleTextInputComponent } from '../../form/controls/simple-text-input/simple-text-input.component';
import { AttachmentPreviewComponent } from '../attachment-preview/attachment-preview.component';
import { ModalService } from '../../modal/modal.service';
import { checkExifOrientation } from '../../../../utils/check-exif-orientation';
import { EditAttachmentComponent } from '../edit-attachment/edit-attachment.component';

class AttachmentItemComponentBase {}

const _AttachmentItemComponentMixinBase = mixinAttachmentHelper(
  AttachmentItemComponentBase
);

@Component({
  selector: 'app-attachment-item',
  templateUrl: './attachment-item.component.html',
  styleUrls: ['./attachment-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ImmomioDropdown,
    ImmomioDropdownTrigger,
    ImmomioDropdownMenu,
    ImmomioDropdownGroup,
    ImmomioDropdownItem,
    ImmomioIconButtonComponent,
    AttachmentPreviewComponent,
    SimpleTextInputComponent,
    FormsModule,
    ReactiveFormsModule,
    SvgIconComponent,
    TranslatePipe,
    FileSizePipe
  ]
})
export class AttachmentItemComponent
  extends _AttachmentItemComponentMixinBase
  implements OnChanges, OnInit
{
  #destroyRef = inject(DestroyRef);
  private sanitizer = inject(DomSanitizer);
  private modalService = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);

  public readonly attachment = input<Attachment>();
  public readonly index = input<number>(undefined);
  public readonly showDownload = input(true);
  public readonly showPreview = input(true);
  public readonly disableDownload = input(false);
  public readonly blockDownload = input(false);
  public readonly showRemove = input(false);
  public readonly isDocument = input(false);
  public readonly hasImages = input(false);
  public readonly orderable = input(false);
  public readonly editable = input(false);

  public readonly remove = output<number>();
  public readonly download = output<Attachment>();
  public readonly preview = output<Attachment>();
  public readonly moveUp = output<Attachment>();
  public readonly moveDown = output<Attachment>();
  public readonly updateAttachment = output<Attachment>();
  public imgSrc: SafeUrl | string;

  public readonly nameInput = viewChild(SimpleTextInputComponent);

  public hasExifMetadata = false;

  public nameControl = new FormControl('');

  public focusName() {
    this.nameInput()?.edit();
  }

  get isPDF() {
    return this.attachment().extension === 'pdf';
  }

  public ngOnInit() {
    this.nameControl.patchValue(this.name);

    this.nameControl.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this.#destroyRef))
      .subscribe(title => this.updateAttachment.emit({ title }));

    checkExifOrientation(this.attachment().file, orientation => {
      this.hasExifMetadata = orientation > -1;
      this.cdr.markForCheck();
    });
  }

  public ngOnChanges() {
    if (!this.isDocument() || this.hasImages()) {
      const attachment = this.attachment();
      this.imgSrc = this.hasFile
        ? this.getSafeUrl(attachment.file || (attachment as Blob))
        : this.attachment().url;
    }

    // Keep the editable title in sync when the bound attachment changes
    // (e.g. after reordering newly added items).
    const newName = this.name;
    if (this.nameControl.value !== newName) {
      this.nameControl.setValue(newName, { emitEvent: false });
    }
  }

  public isPdf(attachment: Attachment) {
    return (
      attachment.type === 'PDF' ||
      (attachment.title && attachment.title.toLowerCase().endsWith('.pdf'))
    );
  }

  public onRemove() {
    this.remove.emit(this.index());
  }

  public onDownload() {
    this.download.emit(this.attachment());
  }

  public onPreview() {
    this.preview.emit(this.attachment());
  }

  public getSafeUrl(file: Blob) {
    return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(file));
  }

  public onMoveUp() {
    if (this.orderable()) {
      this.moveUp.emit(this.attachment());
    }
  }

  public onMoveDown() {
    if (this.orderable()) {
      this.moveDown.emit(this.attachment());
    }
  }

  public editAttachment() {
    this.modalService
      .open(EditAttachmentComponent, {
        data: {
          attachment: { ...this.attachment() },
          removeFn: () => this.remove.emit(this.index())
        }
      })
      .onClose()
      .subscribe((result: { attachment; formValue }) => {
        const title = result.formValue.title || '';
        const rotate = result.attachment.rotate || 0;
        const file = this.attachment().file || result.attachment.file;

        this.updateAttachment.emit({ title, rotate, file });
      });
  }
}
